/**
 * Quest Chain Item
 * Collapsible folder for a quest chain
 * Contains SortableContext for drag-drop within the chain
 */

import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { QuestChain, QuestDefinition } from '@/types';
import { SortableQuestCard } from './SortableQuestCard';
import { questChainManager, customQuestManager } from '@/systems';

interface QuestChainItemProps {
  chain: QuestChain;
  quests: QuestDefinition[];
  isExpanded: boolean;
  onToggle: () => void;
  selectedQuestId: string | null;
  onSelectQuest: (questId: string) => void;
}

export function QuestChainItem({
  chain,
  quests,
  isExpanded,
  onToggle,
  selectedQuestId,
  onSelectQuest,
}: QuestChainItemProps) {
  const handleDelete = () => {
    const questCount = quests.length;
    const confirmMessage = questCount > 0
      ? `Delete chain "${chain.name}"? ${questCount} quest(s) will be moved to Uncategorized.`
      : `Delete empty chain "${chain.name}"?`;

    if (confirm(confirmMessage)) {
      // Move quests to uncategorized before deleting chain
      quests.forEach(quest => {
        customQuestManager.assignQuestToChain(quest.id, null);
      });

      questChainManager.deleteChain(chain.id);
    }
  };

  return (
    <div className="mb-4">
      {/* Chain Header */}
      <div className="flex items-center gap-2 mb-2">
        <button
          onClick={onToggle}
          className="flex items-center gap-2 flex-1 p-2 rounded bg-gray-800 hover:bg-gray-700 transition-colors text-left"
          style={chain.color ? { borderLeft: `4px solid ${chain.color}` } : undefined}
        >
          <span className="text-gray-400">{isExpanded ? '▼' : '▶'}</span>
          {chain.icon && <span className="text-lg">{chain.icon}</span>}
          <span className="font-bold text-white">{chain.name}</span>
          <span className="text-gray-400 text-sm">({quests.length})</span>
          {chain.description && (
            <span className="text-gray-500 text-xs ml-2 truncate">
              {chain.description}
            </span>
          )}
        </button>
        <button
          onClick={handleDelete}
          className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs rounded font-semibold transition-colors"
          title={`Delete chain "${chain.name}"`}
        >
          Delete
        </button>
      </div>

      {/* Chain Quests (shown when expanded) */}
      {isExpanded && (
        <div className="ml-6">
          {quests.length === 0 ? (
            <div className="text-gray-500 text-sm italic p-3 bg-gray-900/50 rounded border border-gray-700">
              No quests in this chain. Drag quests here to organize them.
            </div>
          ) : (
            <SortableContext
              items={quests.map(q => q.id)}
              strategy={verticalListSortingStrategy}
            >
              {quests.map(quest => (
                <SortableQuestCard
                  key={quest.id}
                  quest={quest}
                  isSelected={quest.id === selectedQuestId}
                  onSelect={onSelectQuest}
                />
              ))}
            </SortableContext>
          )}
        </div>
      )}
    </div>
  );
}
